import { useState, useEffect, useCallback, useRef } from "react";

const AI_CONSENT_KEY = "ai_consent_granted";

interface AIConsentState {
  hasConsent: boolean | null; // null = loading
  consentDeniedMessage: boolean;
  needsConsentPrompt: boolean;
}

/**
 * AI Consent — install-based (localStorage only).
 * Consent resets on app reinstall / storage clear, satisfying Apple's requirement.
 * No backend or account dependency.
 */
export function useAIConsent() {
  const [state, setState] = useState<AIConsentState>({
    hasConsent: null,
    consentDeniedMessage: false,
    needsConsentPrompt: false,
  });

  const declinedThisSession = useRef(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(AI_CONSENT_KEY);
    setState(prev => ({
      ...prev,
      hasConsent: stored === "true",
    }));
  }, []);

  const requestConsent = useCallback((): boolean => {
    if (state.hasConsent) return true;

    declinedThisSession.current = false;
    setState(prev => ({
      ...prev,
      consentDeniedMessage: false,
      needsConsentPrompt: true,
    }));
    return false;
  }, [state.hasConsent]);

  const grantConsent = useCallback(async () => {
    localStorage.setItem(AI_CONSENT_KEY, "true");
    setState({
      hasConsent: true,
      consentDeniedMessage: false,
      needsConsentPrompt: false,
    });
    declinedThisSession.current = false;
    console.log("[AIConsent] Consent granted (local)");
  }, []);

  const declineConsent = useCallback(() => {
    declinedThisSession.current = true;
    setState({
      hasConsent: false,
      consentDeniedMessage: true,
      needsConsentPrompt: false,
    });
    console.log("[AIConsent] Consent declined (local)");
  }, []);

  const clearConsentPrompt = useCallback(() => {
    setState(prev => ({ ...prev, needsConsentPrompt: false }));
  }, []);

  const dismissDeniedMessage = useCallback(() => {
    setState(prev => ({ ...prev, consentDeniedMessage: false }));
  }, []);

  const canUseAI = useCallback(() => {
    return state.hasConsent === true;
  }, [state.hasConsent]);

  return {
    hasConsent: state.hasConsent,
    isLoading: state.hasConsent === null,
    consentDeniedMessage: state.consentDeniedMessage,
    needsConsentPrompt: state.needsConsentPrompt,
    requestConsent,
    grantConsent,
    declineConsent,
    dismissDeniedMessage,
    clearConsentPrompt,
    canUseAI,
  };
}
